import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";
import { trpc } from "@/trpc/trpc";
import { Pagination } from "@/components/pagination";
import { SubscriptionDetailDialog } from "@/components/subscription-detail-dialog";
import {
  ALL_SUBSCRIPTION_STATUSES,
  INTERVAL_SHORT,
  SUBSCRIPTION_STATUS,
  type SubscriptionStatus,
  clientLabel,
  daysUntil,
  formatDate,
  zl,
} from "@/components/subscription-labels";

/** Abandoned checkouts are noise for the desk; one click brings them back. */
const DEFAULT_STATUSES = ALL_SUBSCRIPTION_STATUSES.filter(
  (s) => s !== "incomplete",
);

const MANDATE_WARNING_DAYS = 30;
const CREDIT_WARNING_DAYS = 14;

/**
 * Who holds which membership. The table is sorted by what needs attention
 * (dunning first) and every row carries the flags the desk would otherwise
 * have to work out: leaving at period end, retry in progress, mandate about
 * to expire, a credit about to be lost.
 */
export function SubscribersTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [planId, setPlanId] = useState<string>("all");
  const [selectedStatuses, setSelectedStatuses] =
    useState<SubscriptionStatus[]>(DEFAULT_STATUSES);
  const [detailId, setDetailId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const toggleStatus = (status: SubscriptionStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
    setPage(1);
  };

  const statusesParam = useMemo(
    () =>
      selectedStatuses.length === ALL_SUBSCRIPTION_STATUSES.length
        ? undefined
        : selectedStatuses,
    [selectedStatuses],
  );

  const { data: plans } = trpc.subscription.listPlans.useQuery();
  const { data, isLoading } = trpc.subscription.listSubscribers.useQuery(
    {
      page,
      limit: 20,
      statuses: statusesParam,
      planId: planId === "all" ? undefined : planId,
      search: debouncedSearch || undefined,
    },
    { placeholderData: (prev) => prev },
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            placeholder="Szukaj po imieniu, nazwisku lub e-mailu"
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select
          value={planId}
          onValueChange={(v) => {
            setPlanId(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie plany</SelectItem>
            {plans?.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-2">
        {ALL_SUBSCRIPTION_STATUSES.map((s) => {
          const active = selectedStatuses.includes(s);
          return (
            <Button
              key={s}
              size="sm"
              variant={active ? "default" : "outline"}
              onClick={() => toggleStatus(s)}
            >
              {SUBSCRIPTION_STATUS[s].label}
            </Button>
          );
        })}
      </div>

      <Card>
        {isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">Ładowanie...</p>
        ) : data?.items.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            {debouncedSearch || planId !== "all" || statusesParam
              ? "Żadna klientka nie pasuje do filtrów."
              : "Żadna klientka nie wykupiła jeszcze abonamentu."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Klientka</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Płaci</th>
                  <th className="px-4 py-3 font-medium">Od</th>
                  <th className="px-4 py-3 font-medium">Następne obciążenie</th>
                  <th className="px-4 py-3 font-medium">Świadczenia</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              {data?.items.map((group) => (
                <tbody key={group.client.id} className="border-b last:border-0">
                  {group.subscriptions.map((s, i) => {
                    const status = SUBSCRIPTION_STATUS[s.status];
                    const live = !s.endedAt;
                    const mandateDays = s.mandate?.expiresAt
                      ? daysUntil(s.mandate.expiresAt)
                      : null;
                    const creditDays = s.nextCreditExpiresAt
                      ? daysUntil(s.nextCreditExpiresAt)
                      : null;
                    return (
                      <tr
                        key={s.id}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => setDetailId(s.id)}
                      >
                        {i === 0 && (
                          <td
                            className="px-4 py-3 align-top"
                            rowSpan={group.subscriptions.length}
                          >
                            <div className="font-medium">{clientLabel(group.client)}</div>
                            {group.client.email && (
                              <div className="text-xs text-muted-foreground">
                                {group.client.email}
                              </div>
                            )}
                            {group.subscriptions.length > 1 && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                {group.subscriptions.length} abonamenty
                              </div>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3">
                          {s.planName}
                          <div className="text-xs text-muted-foreground">
                            wersja {s.planVersion}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            <Badge variant={status.variant}>{status.label}</Badge>
                            {live && s.cancelAtPeriodEnd && (
                              <Badge variant="outline">rezygnuje</Badge>
                            )}
                            {live && s.dunningAttempt > 0 && (
                              <Badge variant="destructive">
                                próba {s.dunningAttempt}
                              </Badge>
                            )}
                            {live &&
                              mandateDays !== null &&
                              mandateDays <= MANDATE_WARNING_DAYS && (
                                <Badge variant="outline">
                                  {mandateDays < 0
                                    ? "zgoda wygasła"
                                    : `zgoda wygasa za ${mandateDays} dni`}
                                </Badge>
                              )}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {zl(s.price)}
                          <span className="ml-1 text-xs font-normal text-muted-foreground">
                            {INTERVAL_SHORT[s.billingInterval]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {formatDate(s.startedAt)}
                          <div className="text-xs">
                            {s.cyclesBilled}{" "}
                            {s.cyclesBilled === 1 ? "okres opłacony" : "okresów opłaconych"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {s.endedAt
                            ? `zakończona ${formatDate(s.endedAt)}`
                            : s.nextChargeAt
                              ? formatDate(s.nextChargeAt)
                              : s.currentPeriodEnd
                                ? `do ${formatDate(s.currentPeriodEnd)}`
                                : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium">{s.creditsAvailable}</span>
                          <span className="text-xs text-muted-foreground">
                            {" "}do wykorzystania · {s.creditsRedeemed} użyto
                          </span>
                          {creditDays !== null &&
                            s.creditsAvailable > 0 &&
                            creditDays <= CREDIT_WARNING_DAYS && (
                              <div className="text-xs text-destructive">
                                wygasa za {Math.max(0, creditDays)} dni
                              </div>
                            )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetailId(s.id);
                            }}
                          >
                            Szczegóły
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </div>
        )}
        {data && (
          <div className="px-4 pb-4">
            <Pagination
              page={page}
              totalPages={data.totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </Card>

      {data && data.total > 0 && (
        <p className="text-xs text-muted-foreground">
          {data.total} {data.total === 1 ? "klientka" : "klientek"} z abonamentem ·
          kliknij wiersz, żeby zobaczyć obciążenia, świadczenia i historię
        </p>
      )}

      {detailId && (
        <SubscriptionDetailDialog
          subscriptionId={detailId}
          onClose={() => setDetailId(null)}
        />
      )}
    </div>
  );
}
